import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UpdateKycTierLimitInput } from "@/modules/wallet/domain/repo/kyc-limits";
import type { ReviewFlagInput } from "@/modules/wallet/domain/repo/suspicious-flags";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { complianceApi } from "./compliance-client";

export const complianceKeys = {
  all: ["compliance"] as const,
  kycLimits: () => ["compliance", "kyc-limits"] as const,
  kycWalletCounts: () => ["compliance", "kyc-wallet-counts"] as const,
  suspiciousStats: () => ["compliance", "suspicious", "stats"] as const,
  suspiciousList: (q: Partial<OffsetPageQueryDTO>) =>
    ["compliance", "suspicious", "list", q] as const,
};

export function useKycLimitsQuery() {
  return useQuery({
    queryKey: complianceKeys.kycLimits(),
    queryFn: () => complianceApi.listKycLimits(),
  });
}

export function useKycWalletCountsQuery() {
  return useQuery({
    queryKey: complianceKeys.kycWalletCounts(),
    queryFn: () => complianceApi.getKycWalletCounts(),
  });
}

export function useUpdateKycLimitMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateKycTierLimitInput) =>
      complianceApi.updateKycLimit(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: complianceKeys.kycLimits() });
      toast.success("ອັບເດດລິມິດສຳເລັດ");
    },
    onError: () => {
      toast.error("ອັບເດດລິມິດລົ້ມເຫຼວ");
    },
  });
}

export function useSuspiciousStatsQuery() {
  return useQuery({
    queryKey: complianceKeys.suspiciousStats(),
    queryFn: () => complianceApi.getSuspiciousStats(),
  });
}

export function useSuspiciousListQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: complianceKeys.suspiciousList(q),
    queryFn: () => complianceApi.listSuspicious(q),
  });
}

export function useReviewFlagMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReviewFlagInput }) =>
      complianceApi.reviewFlag(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: complianceKeys.all });
      toast.success("ດຳເນີນການສຳເລັດ");
    },
    onError: () => {
      toast.error("ດຳເນີນການລົ້ມເຫຼວ");
    },
  });
}
